import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, Search, Plus, X, Check, Users } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import AttachmentPicker from "../../components/attachments/AttachmentPicker";
import AttachmentBubble from "../../components/attachments/AttachmentBubble";
import { postMessageWithOptionalFiles } from "../../components/attachments/postMessage";

function fmtFull(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function fmtRelative(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ora";
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
    if (diff < 172800) return `ieri`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

function ThreadRow({ active, onClick, title, subtitle, lastAt, unread, iconLetter, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={"lt-thread-row" + (active ? " is-active" : "") + (unread ? " is-unread" : "")}
        >
            <div className="lt-thread-row__logo"><span>{iconLetter || "?"}</span></div>
            <div className="lt-thread-row__body">
                <div className="lt-thread-row__title">
                    {unread && <span className="lt-thread-row__dot" />}
                    <span>{title}</span>
                </div>
                {subtitle && <div className="lt-thread-row__subtitle">{subtitle}</div>}
            </div>
            {lastAt && <div className="lt-thread-row__time">{fmtRelative(lastAt)}</div>}
        </button>
    );
}

export default function PortalComunicazioni() {
    const [me, setMe] = useState(null);
    const [companyThread, setCompanyThread] = useState(null); // thread type=client (canale generale ENERLEFT)
    const [dms, setDms] = useState([]);                        // lista DM misti
    const [selected, setSelected] = useState({ kind: "company", id: null }); // {kind: 'company'|'dm', id}
    const [thread, setThread] = useState(null);                // dettaglio thread selezionato
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    // New conv modal
    const [newConvOpen, setNewConvOpen] = useState(false);
    const [recipientSections, setRecipientSections] = useState(null);
    const [convSelected, setConvSelected] = useState([]);
    const [convQuery, setConvQuery] = useState("");
    const [creatingConv, setCreatingConv] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    }, []);

    const loadIndex = useCallback(async () => {
        try {
            const [cRes, dRes] = await Promise.all([
                api.get("/client/threads/me").catch(() => ({ data: null })),
                api.get("/comunicazioni/dm").catch(() => ({ data: { items: [] } })),
            ]);
            setCompanyThread(cRes.data);
            setDms(dRes.data.items || []);
        } catch { toast.error("Errore caricamento conversazioni"); }
    }, []);

    const loadThread = useCallback(async (sel) => {
        if (!sel || !sel.id) { setThread(null); return; }
        try {
            const url = sel.kind === "dm" ? `/comunicazioni/dm/${sel.id}` : `/client/threads/me`;
            const { data } = await api.get(url);
            setThread({ ...data, __kind: sel.kind });
        } catch {
            toast.error("Conversazione non disponibile");
        }
    }, []);

    useEffect(() => { loadIndex(); }, [loadIndex]);
    // Auto-select company thread on first load
    useEffect(() => {
        if (!selected.id && companyThread?.id) setSelected({ kind: "company", id: companyThread.id });
    }, [companyThread, selected.id]);
    useEffect(() => { loadThread(selected); }, [selected, loadThread]);
    useEffect(() => {
        const id = setInterval(loadIndex, 30000);
        return () => clearInterval(id);
    }, [loadIndex]);

    useEffect(() => {
        if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [thread?.messages?.length, selected.id]);

    const send = async (e) => {
        e?.preventDefault?.();
        const value = text.trim();
        if (!value || sending) return;
        setSending(true);
        try {
            if (selected.kind === "dm") {
                await api.post(`/comunicazioni/dm/${selected.id}/messages`, { text: value });
                setText(""); setFiles([]);
            } else {
                await postMessageWithOptionalFiles("/client/threads/me/messages", { text: value, files });
                setText(""); setFiles([]);
            }
            await loadThread(selected);
            loadIndex();
        } catch (err) {
            if (err?.response?.status === 429) toast.error("Troppi messaggi in poco tempo — riprova tra un minuto");
            else toast.error(err?.response?.data?.detail || "Errore invio");
        } finally {
            setSending(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    };

    const dmTitle = useMemo(() => {
        if (selected.kind !== "dm") return null;
        return dms.find((d) => d.id === selected.id)?.title || thread?.title || "Conversazione";
    }, [selected, dms, thread]);

    return (
        <div className="lt-comms" data-testid="portal-comms-page">
            {/* LEFT — sidebar list */}
            <aside className="lt-comms__list" data-testid="portal-comms-list">
                <div className="lt-comms__search" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, color: "#5F6560", fontSize: 13 }}>Conversazioni</div>
                    <button
                        type="button"
                        className="lt-btn-secondary"
                        onClick={async () => {
                            setNewConvOpen(true);
                            if (!recipientSections) {
                                try {
                                    const { data } = await api.get("/comunicazioni/recipients");
                                    setRecipientSections(data.sections || []);
                                } catch { setRecipientSections([]); }
                            }
                        }}
                        title="Nuova conversazione"
                        aria-label="Nuova conversazione"
                        style={{ width: 40, height: 40, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        data-testid="portal-comms-new-conv-btn"
                    >
                        <Plus size={18} />
                    </button>
                </div>

                <div className="lt-comms__section">
                    <div className="lt-comms__section-h">Canale ENERLEFT</div>
                    {companyThread ? (
                        <ThreadRow
                            active={selected.kind === "company"}
                            onClick={() => setSelected({ kind: "company", id: companyThread.id })}
                            title="Team ENERLEFT"
                            subtitle="Canale generale"
                            lastAt={companyThread.last_message_at}
                            iconLetter="E"
                            testid="portal-comms-thread-company"
                        />
                    ) : <div className="lt-comms__empty">Caricamento…</div>}
                </div>

                <div className="lt-comms__section">
                    <div className="lt-comms__section-h">Conversazioni dirette</div>
                    {dms.length === 0 ? (
                        <div className="lt-comms__empty">Nessuna conversazione diretta. Usa il pulsante &quot;+&quot; per crearne una.</div>
                    ) : dms.map((dm) => {
                        const iconLetter = (dm.title || "?").slice(0, 1).toUpperCase();
                        return (
                            <ThreadRow
                                key={dm.id}
                                active={selected.kind === "dm" && selected.id === dm.id}
                                onClick={() => setSelected({ kind: "dm", id: dm.id })}
                                title={dm.title || "Conversazione"}
                                lastAt={dm.last_message_at}
                                unread={dm.unread}
                                iconLetter={iconLetter}
                                testid={`portal-comms-dm-${dm.id}`}
                            />
                        );
                    })}
                </div>
            </aside>

            {/* RIGHT — chat */}
            <section className="lt-comms__chat" data-testid="portal-comms-chat">
                {!thread ? (
                    <div className="lt-comms__placeholder">Seleziona una conversazione.</div>
                ) : (
                    <>
                        <header className="lt-comms__chat-head" data-testid="portal-comms-chat-head">
                            {selected.kind === "company" ? (
                                <div className="flex items-center gap-3">
                                    <div className="lt-comms__chat-logo lt-comms__chat-logo--team">E</div>
                                    <div>
                                        <div className="lt-comms__chat-title">Team ENERLEFT</div>
                                        <div className="lt-comms__chat-sub">Canale generale con lo staff ENERLEFT</div>
                                    </div>
                                </div>
                            ) : (
                                (() => {
                                    const parts = thread.participants || [];
                                    const isGroup = parts.length >= 3;
                                    const others = parts.filter((p) => p.user_id !== me?.id);
                                    const initials = !isGroup && others[0]?.name
                                        ? others[0].name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()
                                        : null;
                                    return (
                                        <div className="flex items-center gap-3">
                                            <div className="lt-comms__chat-logo lt-comms__chat-logo--dm">
                                                {isGroup ? <Users size={18} strokeWidth={1.75} /> : <span>{initials}</span>}
                                            </div>
                                            <div>
                                                <div className="lt-comms__chat-title">{dmTitle}</div>
                                                <div className="lt-comms__chat-sub">
                                                    {isGroup ? `${parts.length} partecipanti` : "Conversazione diretta"}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()
                            )}
                        </header>

                        <div className="lt-comms__chat-body" data-testid="portal-comms-chat-body">
                            {(thread.messages || []).length === 0 ? (
                                <div className="lt-comms__empty-chat">Ancora nessun messaggio. Scrivi il primo per iniziare.</div>
                            ) : (
                                (thread.messages || []).map((m) => {
                                    const isInternal = m.author?.user_type === "internal";
                                    const isSelf = !isInternal && m.author?.user_id === me?.id;
                                    return (
                                        <div key={m.id} className={"lt-chatmsg " + (isSelf ? "lt-chatmsg--self" : isInternal ? "lt-chatmsg--internal" : "lt-chatmsg--client")} data-testid={`portal-chatmsg-${m.id}`}>
                                            <div className="lt-chatmsg__head">
                                                <span className="lt-chatmsg__author">{m.author?.name || "—"}</span>
                                                <span className="lt-chatmsg__time">{fmtFull(m.created_at)}</span>
                                            </div>
                                            <div className="lt-chatmsg__body">{m.text}</div>
                                            <AttachmentBubble attachments={m.attachments} />
                                        </div>
                                    );
                                })
                            )}
                            <div ref={endRef} />
                        </div>

                        <form className="lt-comms__composer" onSubmit={send} data-testid="portal-comms-composer">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={onKey}
                                placeholder="Scrivi un messaggio…"
                                rows={2}
                                data-testid="portal-comms-composer-text"
                            />
                            {selected.kind === "company" && (
                                <AttachmentPicker files={files} onChange={setFiles} disabled={sending} testIdPrefix="portal-comms-attach" />
                            )}
                            <button type="submit" className="lt-btn-primary" disabled={!text.trim() || sending} data-testid="portal-comms-composer-send">
                                <Send size={14} /> {sending ? "Invio…" : "Invia"}
                            </button>
                        </form>
                    </>
                )}
            </section>

            {newConvOpen && (
                <div className="lt-modal-backdrop" onClick={() => setNewConvOpen(false)} data-testid="portal-comms-new-conv-modal">
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(540px, 92vw)" }}>
                        <div className="lt-modal__head">
                            <h2 className="lt-modal__title">Nuova conversazione</h2>
                            <button className="lt-icon-btn" onClick={() => setNewConvOpen(false)} aria-label="Chiudi"><X size={16} /></button>
                        </div>
                        <div style={{ padding: 20 }}>
                            <p style={{ color: "#5F6560", fontSize: 13.5, margin: "0 0 14px" }}>Seleziona uno o più destinatari.</p>
                            <div style={{ position: "relative", marginBottom: 12 }}>
                                <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8A8F88" }} />
                                <input
                                    type="search"
                                    className="lt-input"
                                    placeholder="Cerca per nome o email…"
                                    value={convQuery}
                                    onChange={(e) => setConvQuery(e.target.value)}
                                    style={{ paddingLeft: 36, width: "100%" }}
                                    data-testid="portal-comms-new-conv-search"
                                />
                            </div>
                            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #E3E5E1", borderRadius: 10 }} data-testid="portal-comms-new-conv-users">
                                {recipientSections === null ? (
                                    <div style={{ padding: 16, color: "#8A8F88", fontSize: 13 }}>Caricamento…</div>
                                ) : recipientSections.length === 0 ? (
                                    <div style={{ padding: 16, color: "#8A8F88", fontSize: 13 }}>Nessun destinatario disponibile.</div>
                                ) : recipientSections.map((section) => {
                                    const q = convQuery.trim().toLowerCase();
                                    const users = (section.users || []).filter((u) =>
                                        !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
                                    );
                                    if (users.length === 0) return null;
                                    return (
                                        <div key={section.key} data-testid={`portal-comms-recipient-section-${section.key}`}>
                                            <div style={{
                                                padding: "10px 14px 6px",
                                                fontFamily: '"IBM Plex Mono", monospace',
                                                fontSize: 11,
                                                letterSpacing: "0.06em",
                                                textTransform: "uppercase",
                                                color: "#8A8F88",
                                                background: "#FAFAF7",
                                                borderTop: "1px solid #EEEFEA",
                                                borderBottom: "1px solid #EEEFEA",
                                            }}>{section.label}</div>
                                            {users.map((u) => {
                                                const key = `${u.user_type}:${u.user_id}`;
                                                const checked = convSelected.some((s) => s.user_type === u.user_type && s.user_id === u.user_id);
                                                const initials = (u.name || "").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                                                return (
                                                    <label key={key} className="lt-user-row" data-testid={`portal-comms-new-conv-user-${u.user_id}`}>
                                                        <span className={"lt-checkbox" + (checked ? " is-checked" : "")}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) => {
                                                                    setConvSelected((prev) => e.target.checked
                                                                        ? [...prev, { user_type: u.user_type, user_id: u.user_id, name: u.name }]
                                                                        : prev.filter((x) => !(x.user_type === u.user_type && x.user_id === u.user_id))
                                                                    );
                                                                }}
                                                            />
                                                            {checked && <Check size={12} color="#181B20" strokeWidth={3} />}
                                                        </span>
                                                        <span className="lt-avatar-initials">{initials || "?"}</span>
                                                        <span style={{ flex: 1, minWidth: 0 }}>
                                                            <span style={{ display: "block", color: "#181B20", fontSize: 14, fontWeight: 500 }}>{u.name}</span>
                                                            <span style={{ display: "block", color: "#5F6560", fontSize: 12 }}>{u.email}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ marginTop: 12 }}>
                                <div style={{ fontSize: 13, color: "#5F6560", marginBottom: 6 }}>Selezionati: {convSelected.length}</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {convSelected.map((s) => (
                                        <span key={`${s.user_type}:${s.user_id}`} className="lt-pill" data-testid={`portal-comms-new-conv-chip-${s.user_id}`}>
                                            {s.name}
                                            <button type="button" onClick={() => setConvSelected((prev) => prev.filter((x) => !(x.user_type === s.user_type && x.user_id === s.user_id)))} aria-label="Rimuovi">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="lt-form-actions" style={{ padding: "12px 20px" }}>
                            <div style={{ flex: 1 }} />
                            <button className="lt-btn-secondary" onClick={() => { setNewConvOpen(false); setConvSelected([]); setConvQuery(""); }} data-testid="portal-comms-new-conv-cancel">Annulla</button>
                            <button
                                className="lt-btn-primary"
                                disabled={convSelected.length === 0 || creatingConv}
                                onClick={async () => {
                                    setCreatingConv(true);
                                    try {
                                        const { data } = await api.post("/comunicazioni/dm", {
                                            participants: convSelected.map((s) => ({ user_type: s.user_type, user_id: s.user_id })),
                                        });
                                        toast.success(data.reused ? "Conversazione già esistente aperta" : "Conversazione creata");
                                        setNewConvOpen(false);
                                        setConvSelected([]);
                                        setConvQuery("");
                                        await loadIndex();
                                        setSelected({ kind: "dm", id: data.id });
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Errore creazione conversazione");
                                    } finally {
                                        setCreatingConv(false);
                                    }
                                }}
                                data-testid="portal-comms-new-conv-submit"
                            >
                                {creatingConv ? "Creazione…" : "Crea conversazione"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
